import { Flex, Title, Box, RingProgress, Group, Text, Stack, Tooltip, ActionIcon } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import Breadcrumb from '@components/breadcrumb';
import TitleBar from '@components/title-bar';
import NextNav from '@components/next-nav';
import RankedAttractions from '@components/vibe-ranked-attractions';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  FIND_ATTR_STATES,
  FIND_VOYAGE_VIBE_STATES_EXPANDED,
  VOYAGE_VIBE_RESULTS,
  WRITE_ATTR_STATES,
} from '@queries/voyage-vibe-state';
import { GET_LAST_STEP, GET_TRIP_PROFILE } from '@queries/trip-profiles';
import LoadingData from '@components/graphql-loading-data';
import GraphqlError from '@components/graphql-error';
import { getTripDaysFromDates } from '@lib/trip-days';
import { useEffect, useState } from 'react';
import { AttractionStateInput } from '@generated/graphql';
import classes from './vibe-results.module.css';

function VoyageVibeResults() {
  const { clientId, tripProfileId = '' } = useParams();
  const [attractionStates, setAttractionStates] = useState<Record<string, AttractionStateInput>>({});
  const [saveState, setSaveState] = useState(false);

  const { data: tripProfileData } = useQuery(GET_TRIP_PROFILE, {
    variables: { id: tripProfileId },
    skip: !tripProfileId,
  });
  const {
    loading,
    error,
    data: vibeResults,
  } = useQuery(VOYAGE_VIBE_RESULTS, { variables: { tripProfileId }, skip: !tripProfileId });
  const {
    loading: loadAttrState,
    error: errorAttrState,
    data: attrStates,
  } = useQuery(FIND_ATTR_STATES, { variables: { tripProfileId }, skip: !tripProfileId });
  const {
    loading: loadingState,
    error: errorState,
    data: attractionTypes,
  } = useQuery(FIND_VOYAGE_VIBE_STATES_EXPANDED, {
    variables: { tripProfileId, specificQuestionIds: ['200'] },
    skip: !tripProfileId,
  });
  const [writeAttractionStates] = useMutation(WRITE_ATTR_STATES, {
    refetchQueries: [
      { query: FIND_ATTR_STATES, variables: { tripProfileId } },
      { query: GET_LAST_STEP, variables: { id: tripProfileId } },
    ],
    awaitRefetchQueries: true,
  });

  const getTripDays = () => {
    const start = tripProfileData?.singleTripProfile?.startDate;
    const end = tripProfileData?.singleTripProfile?.endDate;
    return getTripDaysFromDates(start, end);
  };

  const numInterestedAttractions = Object.values(attractionStates).filter((a) => a.interest && a.interest > 0).length;
  const openSlots = getTripDays() * 2;
  const ringProgressPercent = (numInterestedAttractions / openSlots) * 100;

  useEffect(() => {
    if (attrStates) {
      const updatedStates = attrStates.findAttractionStates.reduce(
        (acc, item) => {
          const { attractionId } = item;
          acc[attractionId] = { attractionId: item.attractionId, interest: item.interest };
          return acc;
        },
        {} as Record<string, AttractionStateInput>
      );

      setAttractionStates(() => {
        if (Object.keys(updatedStates).length >= 4) setSaveState(true);
        return { ...updatedStates };
      });
    }
  }, [attrStates]);

  if (!tripProfileId) return null;
  if (loading || loadingState || loadAttrState) return <LoadingData />;
  if (error || errorState || errorAttrState) return <GraphqlError error={error || errorState || errorAttrState} />;

  const updateAttractionInterest = (attractionId: string, interest: number) => {
    setAttractionStates((prevState) => ({
      ...prevState,
      [attractionId]: { ...prevState[attractionId], interest },
    }));
    if (Object.keys(attractionStates).length >= 4) setSaveState(true);
  };

  const save = async () => {
    if (Object.keys(attractionStates).length < 1) return;
    const attractionStatesArray = Object.keys(attractionStates).map((id) => ({
      interest: attractionStates[id].interest,
      attractionId: id,
    }));
    await writeAttractionStates({
      variables: { tripProfileId, attractionStates: attractionStatesArray },
    });
  };

  return (
    <Flex direction="column" h="calc(100vh - 180px)">
      <Breadcrumb pageStep={5} />
      <Group justify="center">
        <TitleBar pt={6} pb={6} w="80%" />
        <Group gap={4} align="center" mr={2}>
          <RingProgress
            sections={[
              {
                value: ringProgressPercent,
                color: ringProgressPercent >= 75 ? 'green' : 'blue',
              },
            ]}
            size={120}
            thickness={60}
          />
          <Stack gap={0} align="flex-start" justify="center" ml={-2}>
            <Text size="md" fw={700}>
              {numInterestedAttractions} / {openSlots}
            </Text>
            <Group gap={4} align="center" wrap="nowrap">
              <Text size="xs">slots filled</Text>
              <Tooltip
                withArrow
                multiline
                w={260}
                label="Optimal fill is ~3/4 of slots. This allows for longer transfer days and you can delete lower priority sites."
              >
                <ActionIcon variant="transparent" size="md" color="dark" aria-label="Slot progress information">
                  <IconInfoCircle size={25} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Stack>
        </Group>
      </Group>
      <Title order={4} p={4} className={classes.directions}>
        {numInterestedAttractions >= openSlots
          ? 'Every day is filled with interesting attractions!'
          : `You have ${numInterestedAttractions} attractions for ${openSlots} available slots. Select your interest more to add more`}
      </Title>

      <Box style={{ flexGrow: 1, overflow: 'auto', minHeight: 0 }}>
        {attractionTypes && vibeResults && attrStates && (
          <RankedAttractions
            voyageVibeState={attractionTypes?.findVoyageVibeStates[0]}
            vibeResults={vibeResults.findVoyageVibeResults}
            updateAttractionInterest={updateAttractionInterest}
            interestStates={attractionStates}
          />
        )}
      </Box>

      <Box className={classes.navbox}>
        <NextNav
          pt={0}
          saveDisabled={!saveState}
          saveVisible
          next={`/clients/${clientId}/trip-profiles/${tripProfileId}/itinerary`}
          nextDisabled={!saveState}
          saveFunction={save}
        />
      </Box>
    </Flex>
  );
}

export default VoyageVibeResults;
